/**
 * Pure-logic surface for the Phase C circling-area layer.
 *
 * This file is React-free, store-free, MapLibre-free — everything that
 * was hard to unit-test inside CirclingAreaLayer.tsx (because importing
 * the layer pulls in aircraftStore, which touches localStorage at module
 * init time, which doesn't exist in the vitest 'node' env). Split out so
 * the safety-critical decision tree gets clean test coverage.
 *
 * Re-exported from src/map/CirclingAreaLayer.tsx for ergonomics.
 *
 * Safety contract (per data/circling-minima-phase-c-spec.md guardrails):
 *   - No polygon when computeCirclingRadius() falls back (MDA unknown).
 *   - No polygon when the plate has `na_total` restrictions.
 *   - 'note' / time / category-conditioned restrictions surface a ⚠ badge
 *     on the legend chip but DO NOT modify the polygon (parser couldn't
 *     structure them → trust the plate).
 *   - When polygon-clipping empties everything, render only the chip
 *     (the layer caller flips status to 'na-total').
 */

import type { PlanPoint } from '../stores/planStore';
import type { CirclingMinimaApiResponse } from './plates';
import type { NasrAirportDetail, NasrRunway } from './airportNasr';
import {
  computeCirclingRadius,
  type ApproachCategory,
  type CirclingRadiusResult,
} from './circlingRadius';
import { sameAirportIcao } from './procedureExpansion';
import { parseIapIdent } from './procedurePlateMatch';
import {
  buildCirclingStadium,
  buildSectorWedge,
  subtractWedgesFromStadium,
  multiPolygonToFeatureCollection,
  wedgesToFeatureCollection,
} from './circlingGeometry';

// ─── Active IAP resolution ──────────────────────────────────────────────────

export interface ActiveIap {
  airportIcao: string;
  procIdent: string;
  /** Airport ARP from the plan row (PlanPoint.lat/lon). Sector-wedge pivot. */
  arp: { lat: number; lon: number };
}

/**
 * Find the IAP currently loaded in the active plan. Returns null when no
 * IAP-tagged points are in the route. Picks the LAST IAP found (defensive:
 * should be at most one, but if a user replaces an IAP without proper
 * cleanup, honor the most recent).
 */
export function findActiveIap(route: PlanPoint[]): ActiveIap | null {
  let last: { airportIcao: string; procIdent: string } | null = null;
  for (const p of route) {
    const tag = (p as PlanPoint & {
      procedureTag?: { routeType?: string; airportIcao?: string; procIdent?: string };
    }).procedureTag;
    if (tag?.routeType === 'IAP' && tag.airportIcao && tag.procIdent) {
      last = { airportIcao: tag.airportIcao, procIdent: tag.procIdent };
    }
  }
  if (!last) return null;
  // Find the airport row matching the IAP's airportIcao to grab ARP coords.
  for (const p of route) {
    if (p.kind === 'airport' && sameAirportIcao(p.ident, last.airportIcao)) {
      return {
        airportIcao: last.airportIcao,
        procIdent: last.procIdent,
        arp: { lat: p.lat, lon: p.lon },
      };
    }
  }
  // No airport row in the plan for this IAP — defensive abort. The chip
  // depends on the airport's elev for AGL display; without an airport row,
  // we can't safely render the polygon either (no ARP for sector pivot).
  return null;
}

// ─── Runway designator + threshold lookup ──────────────────────────────────

/**
 * Extract the runway designator embedded in a procIdent (e.g. "I10R" →
 * "10R", "R32-Y" → "32", "RY09" → "09", "H22Z" → "22", "VOR-A" → null
 * for circling-only). Returns null when the procIdent has no runway-letter
 * designator (i.e. the approach is circling-only and has no centerline
 * polygon).
 *
 * FAA CIFP idents come in multiple shapes depending on the cycle and the
 * source (raw FAACIFP18 file vs derived blob):
 *   - I28L         (ILS RWY 28L)         — simple
 *   - I10R         (ILS RWY 10R)
 *   - R14-Y        (RNAV (GPS) Y RWY 14) — hyphen + variant suffix (KMFR, live)
 *   - R32-Y        (RNAV (GPS) Y RWY 32) — KMFR live blob form
 *   - H10R-Z       (RNAV (RNP) Z RWY 10R) — hyphen + RNP variant
 *   - R10RY        (RNAV (GPS) Y RWY 10R) — suffix-only (some cycles)
 *   - H10RZ        (RNAV (RNP) Z RWY 10R) — suffix-only
 *   - RY09         (RNAV (GPS) Y RWY 09)  — prefix-variant (older fixture)
 *   - RZ16C, H22Z  — prefix and suffix variant forms
 *   - VOR-A, RNV-D — circling-only (no runway letter)
 *
 * We delegate to parseIapIdent() so this stays in sync with the
 * plate-matching logic; that parser already handles hyphens and trailing
 * Y/Z variants. The 'RY09' / 'RZ16C' prefix form (variant before runway)
 * is uncommon in modern CIFP but we keep a fallback for it so legacy
 * fixtures continue to pass.
 */
export function extractRunwayFromProcIdent(procIdent: string): string | null {
  // Primary: use the same parser as plate matching (handles I/L/R/H/V/N/D/B/S/X/Y/Q/P/G
  // type chars + hyphenated variants + Y/Z suffix variants).
  const parsed = parseIapIdent(procIdent);
  if (parsed && /^\d{1,2}[LCR]?$/.test(parsed.runway)) {
    // Re-pad single-digit runways back to two-digit when the source did
    // (e.g. 'R09-Y' parses runway '9' once leading zeros are stripped; we
    // want the original two-digit form for NASR lookups).
    return parsed.runway.length === 1 ? `0${parsed.runway}` : parsed.runway;
  }
  // Fallback for legacy prefix-variant fixtures like 'RY09', 'RZ16C':
  // strip trailing Z/Y first, then match trailing digits + optional L/C/R.
  const trimmed = procIdent.toUpperCase().replace(/[YZ]$/, '');
  const m = trimmed.match(/(\d{1,2}[LCR]?)$/);
  return m ? m[1] : null;
}

/**
 * Resolve the runway thresholds for the given designator on a NASR airport.
 * Returns both threshold coords + the runway pair label. Null when the
 * designator isn't found.
 */
export function resolveRunwayThresholds(
  runways: NasrRunway[],
  designator: string,
): {
  rwy1: { lat: number; lon: number };
  rwy2: { lat: number; lon: number };
  pairIdent: string;
} | null {
  const designUpper = designator.toUpperCase();
  for (const r of runways) {
    const baseIdent = r.baseEnd?.ident?.toUpperCase() ?? null;
    const recipIdent = r.recipEnd?.ident?.toUpperCase() ?? null;
    if (baseIdent === designUpper || recipIdent === designUpper) {
      const baseLat = r.baseEnd?.endLat;
      const baseLon = r.baseEnd?.endLon;
      const recipLat = r.recipEnd?.endLat;
      const recipLon = r.recipEnd?.endLon;
      if (baseLat == null || baseLon == null || recipLat == null || recipLon == null) {
        return null;
      }
      return {
        rwy1: { lat: baseLat, lon: baseLon },
        rwy2: { lat: recipLat, lon: recipLon },
        pairIdent: r.ident,
      };
    }
  }
  return null;
}

// ─── Restriction-sectors parser ────────────────────────────────────────────

/**
 * Map the 'sectors' field (an unknown jsonb blob from the API) into typed
 * `{ from_deg, to_deg }` rows. Drops rows that don't have both bearings.
 *
 * **Compatibility shim for legacy data:** the Phase A extractor double-
 * stringified some rows (jsonb_typeof = 'string' in the DB, ~534 rows out
 * of 542 sector-restriction variations as of 2026-05-17). We accept both
 * the correct array form AND the legacy stringified form, parsing the
 * latter inline. Without this shim, every existing sector-restriction
 * silently produced an empty wedge list — the polygon rendered as a
 * full unrestricted stadium, missing the magenta hatched-out sector.
 * Backfill SQL re-jsonbs the legacy rows; this shim is the belt-and-
 * suspenders runtime guard.
 */
export function readSectorsField(
  sectors: unknown,
): Array<{ from_deg: number; to_deg: number }> {
  // Legacy stringified jsonb — reparse and recurse.
  if (typeof sectors === 'string') {
    try {
      const reparsed: unknown = JSON.parse(sectors);
      return readSectorsField(reparsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(sectors)) return [];
  const out: Array<{ from_deg: number; to_deg: number }> = [];
  for (const s of sectors) {
    if (typeof s !== 'object' || s == null) continue;
    const obj = s as { from_deg?: unknown; to_deg?: unknown };
    if (typeof obj.from_deg === 'number' && typeof obj.to_deg === 'number') {
      out.push({ from_deg: obj.from_deg, to_deg: obj.to_deg });
    }
  }
  return out;
}

// ─── MDA-by-category resolver (Phase B shape) ──────────────────────────────

export function getMdaForCategory(
  minima: NonNullable<CirclingMinimaApiResponse['minima']>,
  category: ApproachCategory,
): number | null {
  switch (category) {
    case 'A': return minima.circling_mda_a;
    case 'B': return minima.circling_mda_b;
    case 'C': return minima.circling_mda_c;
    case 'D': return minima.circling_mda_d;
    case 'E': return minima.circling_mda_e;
  }
}

// ─── Shared layer state ────────────────────────────────────────────────────

/**
 * What the layer + legend both need to know. Set by the layer on every
 * recompute; both pieces render off this single source of truth.
 */
export interface CirclingAreaState {
  /** Set when an IAP is loaded and we have a status to report. Used by
   *  the legend to decide whether to render at all. */
  active: boolean;
  /** When true, the polygon is rendered. When false, only the chip is
   *  rendered (and the polygon source carries empty FeatureCollections). */
  polygonRendered: boolean;
  /** Aircraft approach category in use for radius lookup. */
  category: ApproachCategory | null;
  /** Radius computation result — the same one the chip uses. */
  radius: CirclingRadiusResult | null;
  /** Categorical state for the chip headline. */
  status:
    | 'ok'              // normal stadium / stadium-minus-sectors
    | 'na-total'        // plate says circling NA — no polygon
    | 'circling-only'   // approach has no runway centerline — no polygon
    | 'data-missing'    // MDA not parsed for active CAT — no polygon
    | 'note-advisory';  // polygon as normal BUT unstructured notes apply
  /** When true, append "⚠ Restrictions apply — see plate" to the chip. */
  hasUnstructuredNotes: boolean;
  /** When true, append "Cycle YYCC (expired)" to the chip. V1: always false. */
  cycleExpired: boolean;
  /** Pretty airport ident for the chip. */
  airportIdent: string | null;
  /** Procedure ident for the chip ("I10R", etc). */
  procIdent: string | null;
}

export const EMPTY_CIRCLING_AREA_STATE: CirclingAreaState = {
  active: false,
  polygonRendered: false,
  category: null,
  radius: null,
  status: 'ok',
  hasUnstructuredNotes: false,
  cycleExpired: false,
  airportIdent: null,
  procIdent: null,
};

// ─── Compute pipeline ──────────────────────────────────────────────────────

export interface CirclingComputeInput {
  airportIcao: string;
  procIdent: string;
  arp: { lat: number; lon: number };
  airport: NasrAirportDetail;
  minima: CirclingMinimaApiResponse | null;
  category: ApproachCategory;
}

export interface CirclingComputeResult {
  state: CirclingAreaState;
  /** GeoJSON FeatureCollection for the legal area. Empty when no polygon. */
  legalFc: GeoJSON.FeatureCollection;
  /** FeatureCollection for excluded sectors (rendered separately). */
  excludedFc: GeoJSON.FeatureCollection;
}

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

/**
 * The pure data → polygon pipeline. Every safety guardrail in the spec
 * lives here:
 *
 *   1. circling-only approach (no runway designator) → no polygon, chip
 *      shows "Circling-only approach"
 *   2. plate has `na` (na_total) restriction → no polygon, chip shows
 *      "Circling NA"
 *   3. computeCirclingRadius returns isFallback → no polygon, chip shows
 *      "data unavailable" (under-claim radius is fine for text but not
 *      for a visual claim)
 *   4. NASR has no matching runway pair → no polygon, chip shows
 *      "data unavailable"
 *   5. normal path: build stadium, subtract sector_restriction wedges,
 *      badge any note/time/category-conditioned variations
 *   6. if subtraction empties everything → status='na-total', no polygon
 */
export function computeCirclingArea(input: CirclingComputeInput): CirclingComputeResult {
  const { airportIcao, procIdent, arp, airport, minima, category } = input;
  const elevFt = airport.elevFt ?? 0;

  // Branch 1: circling-only approach.
  const designator = extractRunwayFromProcIdent(procIdent);
  if (!designator) {
    return {
      state: {
        ...EMPTY_CIRCLING_AREA_STATE,
        active: true,
        polygonRendered: false,
        category,
        airportIdent: airportIcao,
        procIdent,
        status: 'circling-only',
      },
      legalFc: emptyFC(),
      excludedFc: emptyFC(),
    };
  }

  // Branch 2: parsed-restrictions classification.
  const variations = minima?.variations ?? [];
  const filteredVariations = variations.filter((v) => {
    // Mirror the chip's bare-CIRCLING filter (Phase B edge-case #3).
    const trimmed = (v.raw_text ?? '').trim();
    return trimmed.toUpperCase() !== 'CIRCLING' && trimmed.length > 10;
  });
  const hasNaTotal = filteredVariations.some((v) => v.restriction_kind === 'na');
  if (hasNaTotal) {
    return {
      state: {
        ...EMPTY_CIRCLING_AREA_STATE,
        active: true,
        polygonRendered: false,
        category,
        airportIdent: airportIcao,
        procIdent,
        status: 'na-total',
      },
      legalFc: emptyFC(),
      excludedFc: emptyFC(),
    };
  }

  // Branch 3: radius computation.
  const mdaFt = minima?.minima ? getMdaForCategory(minima.minima, category) : null;
  const radius = computeCirclingRadius({
    category,
    circlingMdaFt: mdaFt,
    airportElevFt: elevFt,
  });

  // Branch 3a: fallback radius → DO NOT paint a polygon.
  if (radius.isFallback) {
    return {
      state: {
        ...EMPTY_CIRCLING_AREA_STATE,
        active: true,
        polygonRendered: false,
        category,
        radius,
        airportIdent: airportIcao,
        procIdent,
        status: 'data-missing',
      },
      legalFc: emptyFC(),
      excludedFc: emptyFC(),
    };
  }

  // Branch 4: resolve runway thresholds.
  const thresholds = resolveRunwayThresholds(airport.runways, designator);
  if (!thresholds) {
    return {
      state: {
        ...EMPTY_CIRCLING_AREA_STATE,
        active: true,
        polygonRendered: false,
        category,
        radius,
        airportIdent: airportIcao,
        procIdent,
        status: 'data-missing',
      },
      legalFc: emptyFC(),
      excludedFc: emptyFC(),
    };
  }

  // Branch 5: build stadium + sector wedges.
  const stadium = buildCirclingStadium(thresholds.rwy1, thresholds.rwy2, radius.radiusNm);

  // Wedge extent = 2× radius so the subtraction clips fully through the
  // stadium without leaving a residual sliver at the edge.
  const wedgeExtentNm = Math.max(radius.radiusNm * 2, 3);
  const wedges: GeoJSON.Polygon[] = [];
  let hasUnstructuredNotes = false;
  for (const v of filteredVariations) {
    if (v.restriction_kind === 'sector_restriction') {
      const sectors = readSectorsField(v.sectors);
      for (const sec of sectors) {
        wedges.push(buildSectorWedge(arp, sec.from_deg, sec.to_deg, wedgeExtentNm));
      }
      // If a sector_restriction had no structured sectors, also badge it.
      if (sectors.length === 0) hasUnstructuredNotes = true;
    } else if (
      v.restriction_kind === 'note' ||
      v.restriction_kind === 'time_conditioned' ||
      v.restriction_kind === 'category_conditioned'
    ) {
      // Phase C policy: don't modify polygon, just badge.
      hasUnstructuredNotes = true;
    }
  }

  // Subtract wedges from stadium.
  const legal = subtractWedgesFromStadium(stadium, wedges);

  // If subtraction emptied everything (entire area is sector-NA), suppress
  // the polygon and surface NA status.
  if (legal.length === 0) {
    return {
      state: {
        ...EMPTY_CIRCLING_AREA_STATE,
        active: true,
        polygonRendered: false,
        category,
        radius,
        airportIdent: airportIcao,
        procIdent,
        status: 'na-total',
        hasUnstructuredNotes,
      },
      legalFc: emptyFC(),
      excludedFc: emptyFC(),
    };
  }

  const legalFc = multiPolygonToFeatureCollection(legal, {
    kind: 'legal-circling-area',
    category,
    radiusNm: radius.radiusNm,
    bandLabel: radius.bandLabel,
  });
  const excludedFc = wedgesToFeatureCollection(wedges, {
    kind: 'circling-sector-exclusion',
  });

  return {
    state: {
      active: true,
      polygonRendered: true,
      category,
      radius,
      status: hasUnstructuredNotes ? 'note-advisory' : 'ok',
      hasUnstructuredNotes,
      cycleExpired: false,
      airportIdent: airportIcao,
      procIdent,
    },
    legalFc,
    excludedFc,
  };
}
